import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Building } from 'lucide-react'
import { useAuth } from './AuthProvider'
import { useLoginDepartments } from '../hooks/useLoginDepartments'

/**
 * The login department this session is working in, and Epic's Change Context
 * for moving to another one (F3).
 *
 * Renders NOTHING for an account with no department assignment, which is every
 * account until an administrator makes one. That is the whole safety argument
 * for the feature: absent a department, the product behaves exactly as it did
 * before it existed.
 *
 * The department gates no access. It is working context and attribution — it
 * is what `arrive` / `check_in` stamp on the encounter they open, and what the
 * department census counts.
 */
export function DepartmentSwitcher() {
  const { t } = useTranslation('common')
  const { switchDepartment } = useAuth()
  const { departments, activeDepartmentId, needsSelection } = useLoginDepartments()
  const [isSwitching, setIsSwitching] = useState(false)
  const [failed, setFailed] = useState(false)

  if (departments.length === 0) return null

  const label = (departmentId: number, departmentName: string) =>
    departmentName || t('topBar.departmentLabel', { id: departmentId })

  const onChange = async (value: string) => {
    const departmentId = Number(value)
    if (!departmentId || departmentId === activeDepartmentId) return
    setIsSwitching(true)
    setFailed(false)
    try {
      await switchDepartment(departmentId)
    } catch {
      // The select is driven by `activeDepartmentId`, which only moves once
      // the server has re-minted the session — so a failure leaves the control
      // showing the department still in force. Saying so matters: silently
      // snapping back would read as a misclick rather than a refusal.
      setFailed(true)
    } finally {
      setIsSwitching(false)
    }
  }

  // A sole department is context, not a choice — there is nothing to switch to.
  if (departments.length === 1 && !needsSelection) {
    const only = departments[0]
    return (
      <span
        className="hidden sm:flex items-center gap-1.5 text-sm text-muted-foreground px-2 py-1.5"
        title={t('topBar.activeDepartment')}
      >
        <Building className="h-4 w-4" aria-hidden="true" />
        <span className="sr-only">{t('topBar.activeDepartment')}: </span>
        {label(only.departmentId, only.departmentName)}
      </span>
    )
  }

  return (
    <div className="hidden sm:flex items-center gap-1.5">
      <select
        value={activeDepartmentId ?? ''}
        onChange={(e) => void onChange(e.target.value)}
        disabled={isSwitching}
        className={`text-sm rounded-md border bg-background text-foreground px-2 py-1.5 focus:outline-hidden focus:ring-2 focus:ring-ring disabled:opacity-60 ${
          needsSelection ? 'border-amber-500 text-amber-700 dark:text-amber-400' : 'border-input'
        }`}
        title={t('topBar.selectDepartment')}
        aria-label={t('topBar.selectDepartment')}
      >
        {/* Offered only while nothing is active. Once a department is in force
            there is no way to return to "none" — no endpoint clears it — so an
            option that silently does nothing would be a lie. */}
        {needsSelection && <option value="">{t('topBar.noDepartmentSelected')}</option>}
        {departments.map((d) => (
          <option key={d.departmentId} value={d.departmentId}>
            {label(d.departmentId, d.departmentName)}
          </option>
        ))}
      </select>
      {failed && (
        <span role="status" className="text-xs text-destructive">
          {t('topBar.departmentSwitchFailed')}
        </span>
      )}
    </div>
  )
}
